import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SparklesIcon, ShieldCheckIcon, HeartIcon, UserGroupIcon, ArrowRightIcon, Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import logo from "../Signup/img/logo.svg";
import '../Signup/landing.scss';
import PublicHeader from "components/Header/PublicHeader";
import aboutUsHands from "./img/aboutHands.jpg";

export default function About() {
    useEffect(() => {
        const htmlElement = document.documentElement;
        htmlElement.classList.add("tailwind-html");

        return () => {
            htmlElement.classList.remove("tailwind-html");
        };
    }, []);

    useEffect(() => {
        document.body.classList.add("tailwind-body");

        return () => {
            document.body.classList.remove("tailwind-body");
        };
    }, []);

    const benefits = [
        {
            title: "Trusted Professionals",
            description: "Every therapist is background-checked, licensed, and specifically trained in the nuances of aging and senior care.",
            icon: ShieldCheckIcon,
        },
        {
            title: "Zero Tech Stress",
            description: "No downloads or confusing portals required for your therapy sessions. Just a real person sitting in the room with you.",
            icon: HeartIcon,
        },
        {
            title: "Financial Advocacy",
            description: "We help verify your Medicare benefits before your first visit to ensure transparency in coverage.",
            icon: UserGroupIcon,
        },
    ];

    const careTeamMembers = [
        {
            name: "Jasmine Carter, LCSW",
            role: "Specializing in aging with dignity",
            image: "https://media.istockphoto.com/id/2154927260/uk/фото/senior-man-being-comforted-by-pshychotherapist-during-therapy-session-at-clinic.jpg?s=2048x2048&w=is&k=20&c=xFAr9-0qEYJ1BPmvDPClo-PoiITGxIRkXhdlz14TchY=",
        },
        {
            name: "Marco Ruiz, LMFT",
            role: "Family-first therapy for seniors",
            image: "https://media.istockphoto.com/id/2185364720/uk/фото/психологиня-чат-та-старша-жінка-з-нотатками-та-посмішкою-від-оцінки-психічного-здоровя-на.jpg?s=2048x2048&w=is&k=20&c=_Xs_-fcbAy7MiMFEw0OLbrlViv0ufeEgo_8yAz6q3KI=",
        },
        {
            name: "Priya Desai, PsyD",
            role: "In-home behavioral health support",
            image: "https://media.istockphoto.com/id/1092112802/uk/фото/дружня-медсестра-яка-підтримує-старшу-леді.jpg?s=2048x2048&w=is&k=20&c=2-jJBmKxQY_zL_dXkZEst8FDkujiUueJ0u5hK5_Ea7o=",
        },
    ];

    return (
        <div className="landing-page bg-white">
            <div className="min-h-screen bg-slate-50">
                {/* Header */}
                <PublicHeader />

                {/* Main Content */}
                <main className="relative isolate bg-white">
                    {/* Hero Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-24">
                        <div className="mx-auto max-w-5xl"> {/* Increased width to fit image+text */}

                            {/* Header Section */}
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center rounded-full px-4 py-1 text-sm font-medium bg-sky-50 text-sky-700 ring-1 ring-inset ring-sky-700/10 mb-8">
                                    <SparklesIcon className="h-4 w-4 mr-2 text-sky-600" />
                                    About Emissary Health
                                </div>
                                <h1 className="text-4xl font-bold tracking-tight sm:text-5xl text-slate-900">
                                    We believe quality care shouldn't stop at the clinic door
                                </h1>
                            </div>

                            {/* Content Section: Image + Text Side by Side */}
                            <div className="mt-6 flex flex-col md:flex-row items-center gap-10">

                                {/* Image Column */}
                                <div className="w-full md:w-1/2">
                                    <img
                                        src={aboutUsHands}
                                        alt="About Us"
                                        className="rounded-2xl shadow-xl w-full object-cover h-64 sm:h-80"
                                    />
                                </div>

                                {/* Text Column */}
                                <div className="w-full md:w-1/2 text-lg leading-8 text-gray-600 text-left">
                                    <p>
                                        As we age, life changes. Getting to an office appointment can become a burden, and technology can feel impersonal.
                                    </p>
                                    <p className="mt-4">
                                        At Emissary, we are on a mission to bring compassionate, face-to-face support back to where it belongs: the comfort of your home.
                                    </p>
                                </div>

                            </div>
                        </div>
                    </section>

                    {/* Benefits Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-30">
                        <div className="mx-auto max-w-3xl text-center">
                            <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">
                                What We Offer
                            </h2>
                            <p className="mt-4 text-lg text-gray-600">
                                We are committed to making mental health care accessible and convenient for everyone.
                            </p>
                        </div>
                        <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
                            <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                                {benefits.map((benefit) => (
                                    <div
                                        key={benefit.title}
                                        className="rounded-2xl border border-slate-100 bg-white p-8 shadow-lg hover:shadow-xl transition-all duration-300"
                                    >
                                        <div className="flex items-center gap-x-4 mb-4">
                                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50">
                                                <benefit.icon className="h-6 w-6 text-sky-600" aria-hidden="true" />
                                            </div>
                                            <h3 className="text-xl font-semibold leading-7 text-gray-900">
                                                {benefit.title}
                                            </h3>
                                        </div>
                                        <p className="mt-2 text-base leading-7 text-gray-600">
                                            {benefit.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Care Team Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32">
                        <div className="mx-auto max-w-3xl text-center">
                            <p className="text-sm font-semibold uppercase tracking-widest text-sky-600">
                                OUR PROMISE
                            </p>
                            <h2 className="mt-4 text-3xl font-bold text-slate-900 sm:text-4xl">
                                We only hire therapists we would trust with our own family
                            </h2>
                            <p className="mt-4 text-lg text-gray-600">
                                Credentials matter, but connection matters more. We hand-pick every therapist not just for their license, but for their warmth, patience, and ability to truly listen. Hear from our Chief Clinical Operator on how we select the team that visits your home.
                            </p>
                        </div>

                        <div className="mx-auto mt-16 grid max-w-7xl grid-cols-1 gap-12 lg:grid-cols-2 items-center">

                            <div className="flex flex-col">

                                <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-slate-100 border border-slate-200 shadow-lg flex items-center justify-center group cursor-pointer">

                                    <div className="h-16 w-16 rounded-full bg-white/90 flex items-center justify-center shadow-md transition-transform group-hover:scale-110">
                                        <svg className="h-8 w-8 text-sky-600 ml-1" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8 5v14l11-7z" />
                                        </svg>
                                    </div>
                                    <p className="absolute bottom-4 text-xs text-slate-400 font-medium tracking-wide">VIDEO COMING SOON</p>
                                </div>

                                <p className="mt-4 text-sm text-center text-slate-500 font-medium">
                                    A message from Karen Fortuna, Chief Clinical Officer.
                                </p>
                            </div>

                            <div>
                                <h3 className="text-2xl font-bold text-slate-900 mb-6">
                                    The Emissary Standard
                                </h3>
                                <ul className="space-y-6">
                                    {/* Bullet 1 */}
                                    <li className="flex gap-x-3">
                                        <svg className="h-6 w-5 flex-none text-sky-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-base leading-7 text-gray-600">
                                            <strong className="font-semibold text-slate-900">Matched for Heart:</strong> We hire for empathy, ensuring you feel heard, not just treated.
                                        </span>
                                    </li>
                                    {/* Bullet 2 */}
                                    <li className="flex gap-x-3">
                                        <svg className="h-6 w-5 flex-none text-sky-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-base leading-7 text-gray-600">
                                            <strong className="font-semibold text-slate-900">Specialized Care:</strong> Experts in aging, grief, and life transitions.
                                        </span>
                                    </li>
                                    {/* Bullet 3 */}
                                    <li className="flex gap-x-3">
                                        <svg className="h-6 w-5 flex-none text-sky-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-base leading-7 text-gray-600">
                                            <strong className="font-semibold text-slate-900">Fully Vetted:</strong> Rigorous background checks and license verification.
                                        </span>
                                    </li>
                                </ul>
                            </div>

                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="mx-auto max-w-7xl px-6 lg:px-8 py-24 sm:py-32">
                        <div className="relative overflow-hidden rounded-3xl border border-slate-100 bg-white px-6 py-16 text-center shadow-xl sm:px-16">
                            <div className="absolute inset-0 pointer-events-none">
                                <div className="absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-sky-50/80 to-transparent"></div>
                                <div className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-rose-50/80 to-transparent"></div>
                            </div>
                            <div className="relative">
                                <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                                    Ready to find someone to talk to?
                                </h2>
                                <p className="mx-auto mt-4 max-w-xl text-lg leading-8 text-slate-600">
                                    You don't have to navigate this alone. Check your eligibility today and let us handle the rest.
                                </p>
                                <p className="mx-auto mt-4 max-w-xl text-sm leading-6 text-slate-500">
                                    This is not a guarantee of coverage or payment. We’ll confirm benefits together before your first visit.
                                </p>
                                <div className="mt-8 flex items-center justify-center gap-x-6">
                                    <Link
                                        to="/auth/signup"
                                        className="group relative rounded-full bg-sky-500 px-8 py-4 text-base font-semibold text-white shadow-lg transition hover:bg-sky-600"
                                    >
                                        <span className="relative z-10 flex items-center">
                                            Check Your Eligibility
                                            <ArrowRightIcon className="ml-2 -mr-1 h-5 w-5 inline-block transition-transform duration-300 group-hover:translate-x-1" />
                                        </span>
                                        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-sky-600 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
} 